import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgUiKitModule } from '@infocert/ng-ui-kit';
import { Button, ButtonColor } from '@infocert/ng-ui-kit/models';
import { TranslateService } from '@ngx-translate/core';

export interface CommonModalConfig {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  persistent?: boolean;
  hideButtons?: boolean;
  commercial?: boolean;
  footer?: boolean;
  withSideSection?: boolean;
  className?: string;
  imgSrc?: string;
  autoFocusTarget?: string | boolean;
  restoreFocusTarget?: HTMLElement | string | boolean;
  ariaLabelledby?: string;
  ariaDescribedby?: string;
  ariaLabelClose?: string;
}

export interface CommonModalButtons {
  showCancel?: boolean;
  showConfirm?: boolean;
  cancelText?: string;
  confirmText?: string;
  cancelVariant?: ButtonColor;
  confirmVariant?: ButtonColor;
  closeOnCancel?: boolean;
  closeOnConfirm?: boolean;
}

@Component({
  standalone: true,
  selector: 'app-common-modal',
  imports: [CommonModule, NgUiKitModule],
  template: `
    <ng-ui-kit-modal
      [icId]="modalId"
      [size]="config.size || 'md'"
      [title]="title"
      [visible]="visible"
      [persistent]="config.persistent ?? false"
      [hideButtons]="false"
      [commercial]="config.commercial || false"
      [footer]="config.footer || true"
      [header]="true"
      [withSideSection]="config.withSideSection || false"
      [className]="config.className"
      [imgSrc]="config.imgSrc"
      [autoFocusTarget]="config.autoFocusTarget || 'first-tabbable'"
      [restoreFocusTarget]="
        config.restoreFocusTarget !== undefined
          ? config.restoreFocusTarget
          : true
      "
      [ariaLabelledby]="config.ariaLabelledby"
      [ariaDescribedby]="config.ariaDescribedby"
      [ariaLabelClose]="
        config.ariaLabelClose ||
        this.translateService.instant('ACCESSIBILITY.CLOSE_MODAL')
      "
      [cancelButton]="cancelButtonConfig"
      [confirmButton]="confirmButtonConfig"
      [closeOnCancel]="buttons.closeOnCancel || false"
      [closeOnConfirm]="buttons.closeOnConfirm || false"
      (toggle)="onToggle($event)"
      (onCancel)="onCancel.emit()"
      (onClose)="onClose.emit()"
      (onConfirm)="onConfirm.emit()">
      <!-- Header content -->
      <ng-container header>
        <ng-content select="[slot=header]"></ng-content>
      </ng-container>

      <!-- Main body content -->
      <ng-container body>
        <ng-content></ng-content>
      </ng-container>

      <!-- Side section content -->
      <ng-container sideSection *ngIf="config.withSideSection">
        <ng-content select="[slot=side]"></ng-content>
      </ng-container>

      <!-- Footer content -->
      <ng-container footer *ngIf="config.footer">
        <ng-content select="[slot=footer]"></ng-content>
      </ng-container>
    </ng-ui-kit-modal>
  `,
})
export class CommonModalComponent implements OnInit {
  @Input() modalId: string = `modal-${Date.now()}`;
  @Input() title?: string;
  @Input() visible: boolean = false;
  @Input() config: CommonModalConfig = {};
  @Input() buttons: CommonModalButtons = {};

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onClose = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();
  @Output() onConfirm = new EventEmitter<void>();

  constructor(public translateService: TranslateService) {}
  get shouldHideButtons(): boolean {
    return (
      this.config.hideButtons ||
      (!this.buttons.showCancel && !this.buttons.showConfirm)
    );
  }

  get cancelButtonConfig(): Button {
    if (!this.buttons.showCancel) return null;

    const label =
      this.buttons.cancelText ||
      this.translateService.instant('COMMON.BUTTON.CANCEL');
    return {
      label: label,
      altText: label,
      color: this.buttons.cancelVariant || 'secondary',
    };
  }

  get confirmButtonConfig(): Button {
    if (!this.buttons.showConfirm) return null;

    return {
      label: this.buttons.confirmText || 'Conferma',
      altText:
        this.buttons.confirmText ||
        this.translateService.instant('COMMON.BUTTON.CONFIRM'),
      color: this.buttons.confirmVariant || 'primary',
    };
  }

  ngOnInit() {
    // Auto-generate ID if not provided
    if (!this.modalId) {
      this.modalId = `modal-${Math.random().toString(36).substr(2, 9)}`;
    }
  }

  onToggle(visible: boolean): void {
    this.visible = visible;
    this.visibleChange.emit(visible);

    if (!visible) {
      this.onClose.emit();
    }
  }

  // Public methods for programmatic control
  open(): void {
    this.visible = true;
    this.visibleChange.emit(true);
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onClose.emit();
  }
}
